/**
 * /visao-geral - Dashboard configuravel cargo-aware (Bloco VG).
 *
 * Cada usuario monta seu proprio painel:
 *   - Catalogo de widgets em `_catalogo.js`
 *   - Filtra por permissoes do usuario (RBAC)
 *   - Preferencia (quais ativos + ordem + tamanho) persistida no backend
 *     (`PreferenciaDashboard`, model singleton por usuario)
 *   - Polling 60s (pausa em background)
 *   - Drag-and-drop via dnd-kit no modo "Personalizar"
 *
 * Substitui o placeholder que existia desde SR2.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiCheck, FiRefreshCw, FiSettings } from "react-icons/fi";

import EntityHeader from "../../components/EntityHeader";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import {
  obterPreferenciaDashboard,
  salvarPreferenciaDashboard,
} from "../../services/dashboard";
import { PageContainer } from "../../styles/shared";

import ConfigureSuaEmpresa from "./ConfigureSuaEmpresa";
import GridLayout from "./GridLayout";
import PersonalizarPainel from "./PersonalizarPainel";
import { defaultEssencial, WIDGETS_POR_ID } from "./_catalogo";
import { useDashboardWidgets } from "./useDashboardWidgets";


export default function VisaoGeral() {
  const { request, usuario, isMaster, hasAllPermissions } = useAuth();
  const toast = useToast();

  // LOG.5 (Bloco LOG, auditoria 2026-05-23): inicializa preferencia SINCRONO
  // com defaults baseados em permissoes. Grid renderiza em <50ms sem
  // aguardar o GET /api/me/dashboard-preferencia (que continua acontecendo
  // em background). Quando preferencia salva chega, faz MERGE substituindo
  // os defaults se houver algo persistido. Elimina o "tela carregando"
  // post-login que durava ate ~300ms em rede normal.
  const [preferencia, setPreferencia] = useState(() =>
    defaultEssencial(hasAllPermissions, isMaster),
  );
  const [modoEdicao, setModoEdicao] = useState(false);
  const [personalizarAberto, setPersonalizarAberto] = useState(false);

  // Carrega preferencia salva em background — NAO bloqueia render.
  useEffect(() => {
    let vivo = true;
    obterPreferenciaDashboard(request)
      .then((payload) => {
        if (!vivo) return;
        const widgets = payload?.widgets || [];
        if (widgets.length > 0) {
          // Substitui defaults pela preferencia persistida do usuario.
          setPreferencia(widgets);
        }
        // Se vazia, mantem defaults ja aplicados no useState inicial.
      })
      .catch(() => {
        // Falha silenciosa: defaults ja estao em vigor. Sem fallback extra.
      });
    return () => { vivo = false; };
    // Disparo unico ao montar. hasAllPermissions/isMaster sao funcoes/booleans
    // estaveis vindos do AuthContext; mudanca de usuario forca remount via
    // App.jsx (key=session).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  // Hook orquestrador (fetch + polling 60s)
  const { widgetsAtivos, dados, loading, error, recarregar } =
    useDashboardWidgets({
      preferencia: preferencia || [],
      request,
      isMaster,
      hasAllPermissions,
    });

  // Salva preferencia no backend (debounce simples — salva ao acabar edicao)
  const salvar = async (novaPref) => {
    try {
      await salvarPreferenciaDashboard(request, novaPref);
    } catch (err) {
      toast.error(err?.message || "Erro ao salvar preferencia.");
    }
  };

  // Acao: reordenar (drag-and-drop callback)
  const handleReordenar = (novaOrdemIds) => {
    if (!preferencia) return;
    const mapa = preferencia.reduce((acc, p) => {
      acc[p.id] = p;
      return acc;
    }, {});
    const novaPref = novaOrdemIds.map((id, idx) => ({
      ...(mapa[id] || { id, tamanho: WIDGETS_POR_ID[id]?.tamanho_default || "M" }),
      posicao: idx,
    }));
    setPreferencia(novaPref);
    salvar(novaPref);
  };

  // Acao: remover widget (botao X em modo edicao)
  const handleRemover = (widgetId) => {
    if (!preferencia) return;
    const novaPref = preferencia
      .filter((p) => p.id !== widgetId)
      .map((p, idx) => ({ ...p, posicao: idx }));
    setPreferencia(novaPref);
    salvar(novaPref);
  };

  // Acao: toggle de widget no painel "Personalizar"
  const handleToggleWidget = (widgetId, ativo) => {
    if (!preferencia) return;
    const jaTem = preferencia.some((p) => p.id === widgetId);
    let novaPref;
    if (ativo && !jaTem) {
      const def = WIDGETS_POR_ID[widgetId];
      novaPref = [
        ...preferencia,
        {
          id: widgetId,
          posicao: preferencia.length,
          tamanho: def?.tamanho_default || "M",
        },
      ];
    } else if (!ativo && jaTem) {
      novaPref = preferencia
        .filter((p) => p.id !== widgetId)
        .map((p, idx) => ({ ...p, posicao: idx }));
    } else {
      return;
    }
    setPreferencia(novaPref);
    salvar(novaPref);
  };

  const contexto = useMemo(() => ({ usuario }), [usuario]);

  const idsAtivosSet = useMemo(
    () => new Set((preferencia || []).map((p) => p.id)),
    [preferencia],
  );

  return (
    <PageContainer>
      {/* Alvo do passo "Visão Geral" do tour (ADR-0104): o cabeçalho com o Personalizar. */}
      <div data-tour="visao-geral">
      <EntityHeader
        title="Visão Geral"
        subtitle="Painel personalizado com seus indicadores"
        primaryAction={
          modoEdicao
            ? {
                label: "Concluir",
                icon: <FiCheck size={14} />,
                onClick: () => setModoEdicao(false),
              }
            : {
                label: "Personalizar",
                icon: <FiSettings size={14} />,
                onClick: () => {
                  setModoEdicao(true);
                  setPersonalizarAberto(true);
                },
              }
        }
        actions={[
          {
            label: "Atualizar agora",
            icon: <FiRefreshCw size={14} />,
            onClick: () => recarregar(),
          },
        ]}
      />
      </div>

      {/* Onboarding (ADR-0104): só para quem administra, até a empresa chegar a 100%. */}
      <ConfigureSuaEmpresa />

      {/* LOG.5: grid renderiza imediato com defaults; merge da preferencia
          salva acontece em background sem bloquear UI. */}
      <GridLayout
        widgetsAtivos={widgetsAtivos}
        contexto={contexto}
        dados={dados}
        loading={loading}
        error={error}
        modoEdicao={modoEdicao}
        onReordenar={handleReordenar}
        onRemover={handleRemover}
      />

      <PersonalizarPainel
        aberto={personalizarAberto}
        onFechar={() => setPersonalizarAberto(false)}
        idsAtivos={idsAtivosSet}
        isMaster={isMaster}
        hasAllPermissions={hasAllPermissions}
        onToggle={handleToggleWidget}
      />
    </PageContainer>
  );
}
